import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {t} from 'i18next';
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import _ from 'lodash';
import './ESIndexing.scss';
import IngestionRadioOptions from '../component/IngestionRadioOptions/IngestionRadioOptions';
import IngetionTabPanelHeader from '../component/TabPanelHeader/IngetionTabPanelHeader';
import {
  useESIndexingConfigInfo,
  useESIndexingListData,
  useRunESIndexing,
} from '../../../hooks/ingestions/useIngestions';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  ESIndexingRunOptionType,
  ESIndexListItemType,
  ToastMessageConfigType,
} from '../../../api/types/ingestionApi.type';
import ESIndexErrorListModal from '../component/ESIndexErrorListModal/ESIndexErrorListModal';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import CustomNoRowsOverlay from '../component/CustomNoRowsOverlay/CustomNoRowsOverlay';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import CellWrapper from '../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';

const stringPath = 'ingestions.esIndexing.';

const ES_INDEXING_STATUS_WITH_ERROR_TYPES = new Set([
  'ES_INDEXING_COMPLETED_WITH_ERRORS',
  'ES_REINDEXING_COMPLETED_WITH_ERRORS',
  'ES_REINDEXING_FAILED',
  'ES_INDEXING_FAILED',
]);

type StringMap = Record<string, string>;
const indexingStatusCaptionMap: StringMap = {
  ES_INDEXING_COMPLETED_WITH_ERRORS: 'statusIndexingComlpetedWithError',
  ES_REINDEXING_COMPLETED_WITH_ERRORS: 'statusReindexingCompletedWithError',
  ES_INDEXING_COMPLETED: 'statusIndexingCompleted',
  ES_REINDEXING_COMPLETED: 'statusReindexingCompleted',
  ES_REINDEXING_INPROGRESS: 'statusReindexingInProgress',
  ES_INDEXING_INPROGRESS: 'statusIndexingInProgress',
  ES_REINDEXING_FAILED: 'statusReIndexingFaield',
  ES_INDEXING_FAILED: 'statusIndexingFaield',
};

const defaultSortColumn = {
  field: 'startTime',
  sort: 'desc',
};

const NoRowsOverlay = () => {
  return (
    <CustomNoRowsOverlay message={t(stringPath + 'noQueryReturnDataMsg')} />
  );
};

const EsIndexing = () => {
  const [selectedRunIndexType, setSelectedRunIndexType] = useState<string>('');
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(PAGE_SIZE);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    defaultSortColumn,
  ] as GridSortModel);
  const [toastMsgConfig, setToastMsgConfig] = useState<
    ToastMessageConfigType & {show: boolean}
  >({
    show: false,
  });

  const [selectedErrorIndexRunId, setSelectedErrorIndexRunId] = useState<
    number | null
  >(null);

  // Fetch ESIndexing Config api call
  const esIndexingConfigResult = useESIndexingConfigInfo();
  const esIndexingConfig = esIndexingConfigResult?.data ?? {
    canRun: false,
    canRunIndexWE: 0,
    canFullReIndex: 0,
    esIngestionCount: 0,
  };

  // Fetch ESIndexingList Data call
  const {
    data: esIndexingListData,
    isLoading: isESIndexingLoading,
    refetch: refreshESIndexingList,
    isError: isESIndexingError,
    error: esIndexingError,
  } = useESIndexingListData({
    pageNumber: pageNumber,
    pageSize: itemPerPage,
    sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
    direction: sortModel[0]?.sort ?? defaultSortColumn.sort,
  });

  // Run ESIndexing Request
  const {mutate: runIndexingReq, isPending: isRunESIndexingLoading} =
    useRunESIndexing();

  // Track esIndexing fetch request error and show toast for it
  useEffect(() => {
    if (isESIndexingError && esIndexingError) {
      showToast({
        message: t(stringPath + 'toastMsg.loadingEsIndexingFailed'),
        type: ToastType.Error,
      });
    }
  }, [isESIndexingLoading, isESIndexingError, esIndexingError]);

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'indexName',
        headerName: t('ingestions.common.indexName'),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const indexName = params.row?.indexName;
          return (
            <span
              // title={indexName || 'Standard'}
              data-testid={`es-indexing-indexing-${params.id}`}
            >
              {indexName || 'Standard'}
            </span>
          );
        },
      },
      {
        field: 'runType',
        headerName: t('ingestions.common.runType'),
        flex: 1,
      },
      {
        field: 'startTime',
        headerName: t('ingestions.common.startTime'),
        flex: 1,
      },
      {
        field: 'endTime',
        headerName: t('ingestions.common.endTime'),
        flex: 1,
      },

      {
        field: 'status',
        headerName: t('ingestions.common.status'),
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const {status} = params.row;
          let statusCaption = status;
          let className = '';

          if (indexingStatusCaptionMap[status])
            statusCaption = t(stringPath + indexingStatusCaptionMap[status]);

          if (ES_INDEXING_STATUS_WITH_ERROR_TYPES.has(status)) {
            className = 'status-error-caption';
          }

          return (
            <CellWrapper
              toolTipContent={params.value}
              cellParams={params}
              dataTestId={`es-indexing-cell-wrapper-${params.id}`}
            >
              <span
                // title={statusCaption}
                className={className}
                data-testid={`es-indexing-span-status-${params.id}`}
              >
                {statusCaption}
              </span>
            </CellWrapper>
          );
        },
      },
      {
        field: 'errorMessage',
        headerName: t('ingestions.common.errorMessage'),
        headerClassName: 'last-column',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => {
          const {errorMessage} = params.row as ESIndexListItemType;

          return (
            <CellWrapper
              toolTipContent={params.value}
              cellParams={params}
              dataTestId={`es-indexing-error-cell-wrapper-${params.id}`}
            >
              <span
                // title={errorMessage}
                className="error-message-caption"
                data-testid={`es-indexing-error-mess-${params.id}`}
              >
                {_.isEmpty(errorMessage) ? '--' : errorMessage}
              </span>
            </CellWrapper>
          );
        },
      },
    ];
  }, [t]);

  const reIndexingOptions = useMemo(() => {
    const runIndexContent = t(stringPath + 'options.runIndexing', {
      returnObjects: true,
    }) as {
      label: string;
      toolTip: string;
    };

    const reindexProjectContent = t(stringPath + 'options.reindexProject', {
      returnObjects: true,
    }) as {
      label: string;
      toolTip: string;
    };

    return [
      {
        label: runIndexContent.label,
        tooltip: runIndexContent.toolTip,
        value: 'INDEXING_RUNINDEXING',
        disabeld:
          !esIndexingConfig.canRun || esIndexingConfig.canRunIndexWE !== 1,
      },
      {
        label: reindexProjectContent.label,
        tooltip: reindexProjectContent.toolTip,
        value: 'INDEXING_REINDEX',
        disabeld:
          !esIndexingConfig.canRun || esIndexingConfig.canFullReIndex !== 1,
      },
    ];
  }, [esIndexingConfig, t]);

  const dataGridClass = useMemo(() => {
    if (isESIndexingLoading) return 'index-grid-loading';

    if ((esIndexingListData?.totalRecords ?? 0) === 0)
      return 'index-grid-no-row-overlay';

    return 'index-grid';
  }, [isESIndexingLoading, esIndexingListData]);

  const gridRows = useMemo(() => {
    return (esIndexingListData?.data ?? []).map((item) => ({
      ...item,
      indexName: item?.indexName || 'Standard',
    }));
  }, [esIndexingListData]);

  const handleIndexClick = (params: GridCellParams) => {
    const {field, row}: {field: string; row: ESIndexListItemType} = params;

    if (
      field === 'status' &&
      ES_INDEXING_STATUS_WITH_ERROR_TYPES.has(row.status)
    ) {
      setSelectedErrorIndexRunId(row.esIndexRunID);
    }
  };

  const handleRunIndexClick = (value: string) => {
    setSelectedRunIndexType(value);
    runIndexingReq(
      {selectedValue: value as ESIndexingRunOptionType},
      {
        onSuccess: () => {
          setSelectedRunIndexType('');
          showToast({
            message: t(stringPath + 'toastMsg.runStartedSuccessMsg'),
            type: ToastType.Success,
          });
          setPageNumber(1);
          setTimeout(() => {
            refreshESIndexingList();
          }, 250);
          esIndexingConfigResult?.refetch();
        },
        onError: () => {
          showToast({
            message: t(stringPath + 'toastMsg.rerunFailed'),
            type: ToastType.Error,
          });
        },
      }
    );
  };

  const renderTopHeader = () => {
    return (
      <Box className="es-top-header" display="flex" flexDirection="column">
        <IngetionTabPanelHeader
          title={t(stringPath + 'runOneOfOptionCaption')}
          subTitle={t(stringPath + 'newIngestions', {
            number: esIndexingConfig.esIngestionCount,
          })}
        />

        {/** Reindex project options */}
        <IngestionRadioOptions
          label={t(stringPath + 'reindexProjectData')}
          options={reIndexingOptions}
          canRun={esIndexingConfig.canRun}
          onRun={handleRunIndexClick}
          defaultValue={selectedRunIndexType}
          runLoading={isRunESIndexingLoading}
        />
      </Box>
    );
  };

  const showToast = useCallback((config: ToastMessageConfigType) => {
    setToastMsgConfig({
      show: true,
      title: config.title,
      message: config.message,
      type: config.type,
    });
  }, []);

  return (
    <Box className="es-indexing-container">
      {renderTopHeader()}
      <Box className="index-list-grid-container">
        <ArrDataGrid
          rows={gridRows}
          rowCount={esIndexingListData?.totalRecords ?? 0}
          columns={columns}
          paginationMode="server"
          primaryKey="esIndexRunID"
          loading={isESIndexingLoading}
          showPerPageOptions
          pageSizeOptions={[10]}
          itemsPerPage={itemPerPage}
          onPageChange={(value) => setPageNumber(value)}
          onSortModelChange={setSortModel}
          currentPage={pageNumber}
          setItemsPerPage={setItemPerPage}
          onCellClick={handleIndexClick}
          onCellKeyDown={(params, event) => {
            if (event.key === 'Enter') {
              handleIndexClick(params);
            }
          }}
          entityType={t('ingestions.common.entries')}
          className={dataGridClass}
          hideFooter={(esIndexingListData?.totalRecords ?? 0) === 0}
          initialState={{
            sorting: {
              sortModel: [defaultSortColumn] as GridSortModel,
            },
          }}
          alwaysShowPagination
          noRowsOverlayCustomText={t(stringPath + 'noQueryReturnDataMsg')}
        />
      </Box>
      {selectedErrorIndexRunId && (
        <ESIndexErrorListModal
          showModal
          ingestionId={selectedErrorIndexRunId}
          onClose={() => {
            setSelectedErrorIndexRunId(null);
          }}
          showToast={showToast}
          showExportSuccessToast={(config) => {
            showToast(config);
            setSelectedErrorIndexRunId(null);
          }}
        />
      )}
      {toastMsgConfig.show && (
        <ArrToastNotification
          onClose={() => setToastMsgConfig({show: false})}
          status={[toastMsgConfig.type ?? ToastType.Success]}
          message={toastMsgConfig.message ?? ''}
          title={toastMsgConfig.title}
        />
      )}
    </Box>
  );
};

export default EsIndexing;
